import React, { useState, useEffect } from 'react';
import styles from './PaymentConfirmModal.module.css';
import BkashCreatePaymentAPICall from '../../../action/BkashCreatePaymentAPICall';
import AxiosUrl from '../../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import RuSu from '../../../img/rusu.jpg';
import 'react-toastify/dist/ReactToastify.css';

function PaymentConfirmModal({ setOpenModal, data, packageName }) {
    const uid = localStorage.getItem('id');
    const [loading, setLoading] = useState(false);
    const [priceLoading, setPriceLoading] = useState(true);
    const [priceData, setPriceData] = useState(null);
    const [bkash] = useState('');

    // Fetch price preview on mount
    useEffect(() => {
        const fetchPrice = async () => {
            try {
                const res = await AxiosUrl.post(`/price-preview/${uid}`, {
                    packageType: data.packageType,
                    billingPeriod: data.billingPeriod,
                });

                if (res.data.success) {
                    setPriceData(res.data.data);
                }
            } catch (error) {
                console.error('Failed to fetch price:', error);
                toast.error('Failed to load price information');
            } finally {
                setPriceLoading(false);
            }
        };

        fetchPrice();
    }, [uid, data.packageType, data.billingPeriod]);

    const subscribe = async () => {
        setLoading(true);
        try {
            // FIX: Send new secure format (packageType + billingPeriod)
            const res = await AxiosUrl.post(`/pay-bill/${uid}`, {
                packageType: data.packageType,
                billingPeriod: data.billingPeriod,
            });

            // Backend returns: { success, data: { UID, Amount, Discount, PackageType } }
            if (res.data.success) {
                // Remember where to return the user if the bKash payment fails.
                localStorage.setItem('payReturn', '/billing');
                BkashCreatePaymentAPICall(
                    res.data.data.Amount,
                    res.data.data.UID,
                    res.data.data.UID
                );
                setOpenModal(false);
            } else {
                toast.error(res.data.error || 'Payment initiation failed');
            }
        } catch (error) {
            console.error(error);
            const errorMessage = error.response?.data?.error || 'Payment initiation failed. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const billingCycle = data.billingPeriod === 'yearly' ? 'বাৎসরিক' : 'মাসিক';

    return (
        <div className={styles.modalOverlay}>
            <ToastContainer position="top-center" />
            <div className={styles.modalCard}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>প্যাকেজ কনফার্ম করুন</h2>
                    <button
                        className={styles.closeButton}
                        onClick={() => setOpenModal(false)}
                        aria-label="Close"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className={styles.modalContent}>
                    {/* Logo Section */}
                    <div className={styles.logoSection}>
                        <div className={styles.logoWrapper}>
                            <img
                                src={RuSu}
                                alt="RUSU BD"
                                className={styles.logo}
                            />
                        </div>
                        <h3 className={styles.merchantName}>RUSU BD</h3>
                    </div>

                    {/* Package Details Card */}
                    <div className={styles.detailsCard}>
                        <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>প্যাকেজ</span>
                            <span className={styles.detailValue}>{packageName}</span>
                        </div>
                        <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>বিলিং সাইকেল</span>
                            <span className={styles.detailValue}>{billingCycle}</span>
                        </div>

                        {/* Show original price and discount if applicable */}
                        {priceData && priceData.discount > 0 && (
                            <>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>মূল মূল্য</span>
                                    <span className={styles.detailValue} style={{ textDecoration: 'line-through', opacity: 0.6 }}>
                                        ৳{priceData.originalPrice.toLocaleString()}
                                    </span>
                                </div>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>ছাড়</span>
                                    <span className={styles.detailValue} style={{ color: '#00a854' }}>
                                        {priceData.discount}% (৳{priceData.savings.toLocaleString()})
                                    </span>
                                </div>
                            </>
                        )}

                        {/* Outstanding extra-order charges (৳1/order over the monthly limit) */}
                        {priceData && priceData.extraOrderCharge > 0 && (
                            <>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>প্যাকেজ মূল্য</span>
                                    <span className={styles.detailValue}>
                                        ৳{priceData.finalPrice.toLocaleString()}
                                    </span>
                                </div>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>বকেয়া এক্সট্রা অর্ডার চার্জ</span>
                                    <span className={styles.detailValue} style={{ color: '#b91c1c' }}>
                                        ৳{priceData.extraOrderCharge.toLocaleString()}
                                    </span>
                                </div>
                            </>
                        )}

                        <div className={styles.divider}></div>
                        <div className={styles.detailRow}>
                            <span className={styles.detailLabelTotal}>মোট পরিমাণ</span>
                            <span className={styles.detailValueTotal}>
                                {priceLoading ? (
                                    'লোড হচ্ছে...'
                                ) : priceData ? (
                                    `৳${(priceData.totalPayable ?? priceData.finalPrice).toLocaleString()}`
                                ) : (
                                    'N/A'
                                )}
                            </span>
                        </div>
                    </div>

                    {/* Payment Method */}
                    <div className={styles.paymentMethod}>
                        <div className={styles.paymentLabel}>
                            <svg className={styles.paymentIcon} width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M3 6a3 3 0 013-3h8a3 3 0 013 3v8a3 3 0 01-3 3H6a3 3 0 01-3-3V6z" stroke="currentColor" strokeWidth="2"/>
                                <path d="M3 8h14M7 12h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            <span>পেমেন্ট মেথড</span>
                        </div>
                        <div className={styles.bkashBadge}>
                            <span className={styles.bkashText}>bKash</span>
                        </div>
                    </div>

                    {/* Info Banner */}
                    <div className={styles.infoBanner}>
                        <svg className={styles.infoIcon} width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                        </svg>
                        <p className={styles.infoText}>
                            আপনার bKash একাউন্ট থেকে টাকা চার্জ করা হবে।
                            পেমেন্ট সম্পন্ন করতে নিচের বাটনে ক্লিক করুন।
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.modalFooter}>
                    <button
                        className={styles.cancelButton}
                        onClick={() => setOpenModal(false)}
                        disabled={loading}
                    >
                        বাতিল করুন
                    </button>
                    <button
                        className={styles.confirmButton}
                        onClick={subscribe}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className={styles.spinner}></span>
                                <span>প্রসেসিং...</span>
                            </>
                        ) : (
                            <>
                                <svg className={styles.buttonIcon} width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 3.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 9H13a1 1 0 100-2H8.414l1.293-1.293z" clipRule="evenodd"/>
                                </svg>
                                <span>bKash দিয়ে পেমেন্ট করুন</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PaymentConfirmModal;
